import {
  createApiFactory,
  createPlugin,
  createRoutableExtension,
  configApiRef,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';

import { auditLogApiRef } from './api';
import { AuditLogApiClient } from './api/client';
import { MockAuditLogApi } from './api/mock';
import { rootRouteRef } from './routes';

/**
 * Audit Log plugin. Registers the `auditLogApiRef` implementation: fixtures
 * via `MockAuditLogApi` in development (or when `auditLog.useMockApi: true`),
 * the real `AuditLogApiClient` otherwise. When `auditLog.simulateUnsubscribed`
 * is true, the mock answers 403 like a tenant without a subscription, to demo
 * the setup guide.
 */
export const auditLogPlugin = createPlugin({
  id: 'audit-log',
  apis: [
    createApiFactory({
      api: auditLogApiRef,
      deps: {
        configApi: configApiRef,
        discoveryApi: discoveryApiRef,
        fetchApi: fetchApiRef,
      },
      // Stage 1 ships the mock; going live is config-only — the app proxy
      // (/api/audit-log) injects the x-api-key header server-side.
      factory: ({ configApi, discoveryApi, fetchApi }) =>
        (configApi.getOptionalBoolean('auditLog.useMockApi') ??
        process.env.NODE_ENV === 'development')
          ? new MockAuditLogApi({
              simulateUnsubscribed: configApi.getOptionalBoolean(
                'auditLog.simulateUnsubscribed',
              ),
            })
          : new AuditLogApiClient({ discoveryApi, fetchApi }),
    }),
  ],
  routes: {
    root: rootRouteRef,
  },
});

/** Routable search page for audit events, mounted at `/audit-log/*`. */
export const AuditLogPage = auditLogPlugin.provide(
  createRoutableExtension({
    name: 'AuditLogPage',
    component: () =>
      import('./components/AuditLogPage').then(m => m.AuditLogPageContent),
    mountPoint: rootRouteRef,
  }),
);
